import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Square, Repeat, Heart, Lock, Church, CheckCircle2, Music } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { StaffViewer } from "@/components/StaffViewer";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { playPitch } from "@/lib/audio";
import { api } from "@/lib/api";
import { startTrack, stopTrack, keyPc } from "@/lib/playalongEngine";
import { useAuth } from "@/context/AuthContext";
import { GOSPEL_LICKS } from "@/data/gospelLicks";
import { GOSPEL_COURSES } from "@/data/curriculum";
import { TRACKS, LESSON_TRACKS } from "@/data/playalong";
import { STYLES } from "@/data/playalongStyles";

const BEATS = { eighth: 0.5, quarter: 1, half: 2, whole: 4 };
const SPEEDS = [50, 75, 100];
const chip = (on) => `rounded-full border px-3 py-1 text-xs transition-colors ${on ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:border-primary/60"}`;
const backingFor = (lick) => TRACKS[lick.backing || LESSON_TRACKS[lick.lessonId]?.t];
const tonicPc = (key) => { const m = key.match(/^([A-G])(♭|♯)?/); return m ? keyPc(m[1] + (m[2] === "♭" ? "b" : m[2] === "♯" ? "#" : "")) : null; };
const shiftTo = (lick, track) => {
  const pc = tonicPc(lick.key);
  if (pc == null) return 0;
  const d = (((pc - keyPc(track.key)) % 12) + 12) % 12;
  return d > 6 ? d - 12 : d;
};

let activeCard = null;

const LickCard = ({ lick, bpm, saved, mastered, onSave, onMaster, locked }) => {
  const [step, setStep] = useState(-1);
  const [loop, setLoop] = useState(false);
  const [speed, setSpeed] = useState(100);
  const [band, setBand] = useState(false);
  const timers = useRef([]);
  const live = useRef({});
  live.current = { ...live.current, loop, tempo: (bpm * speed) / 100 };
  const track = backingFor(lick);
  const lickBeats = lick.notes.reduce((t, nt) => t + (BEATS[nt.duration] || 1), 0);
  const clear = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  const reset = () => { clear(); setStep(-1); };
  const me = useRef({}).current;
  me.reset = reset;
  const stop = () => { reset(); if (activeCard === me) { stopTrack(); activeCard = null; } };
  useEffect(() => () => { clear(); if (activeCard === me) { stopTrack(); activeCard = null; } }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const once = (tempo) => {
    clear();
    const beat = 60 / tempo;
    let t = 0;
    lick.notes.forEach((nt, i) => {
      const d = (BEATS[nt.duration] || 1) * beat;
      timers.current.push(setTimeout(() => { setStep(i); (nt.pitches || [nt.pitch]).forEach((p) => playPitch(p, Math.min(1.4, d * 0.95))); }, t * 1000));
      t += d;
    });
    return t;
  };
  const playSolo = () => {
    const t = once(live.current.tempo);
    timers.current.push(setTimeout(() => (live.current.loop ? playSolo() : setStep(-1)), t * 1000 + 300));
  };
  const playBand = async () => {
    const tempo = live.current.tempo;
    const bars = Math.max(1, Math.ceil(lickBeats / STYLES[track.style].beats));
    let count = 0;
    await startTrack(track, {
      bpm: tempo, semitones: shiftTo(lick, track), lead: "voice", bandVolume: 0.45, clickVolume: 0.35, metronome: false,
      parts: { drums: true, bass: true, keys: lick.inst === "guitarists", guitar: lick.inst === "pianists", lead: false },
      onBar: () => {
        if (count % bars === 0) {
          if (count > 0 && !live.current.loop) { stop(); return; }
          once(live.current.tempo);
        }
        count += 1;
      },
    });
    setStep(0);
  };
  const play = () => {
    if (activeCard && activeCard !== me) { activeCard.reset(); stopTrack(); }
    reset();
    activeCard = me;
    if (band && track) playBand(); else playSolo();
  };
  return (
    <div className={`rounded-2xl border bg-card p-5 ${mastered ? "border-primary/60" : "border-border"}`} data-testid={`lick-${lick.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-primary">{lick.level} · {lick.key}</p>
          <h3 className="mt-1 font-serif text-xl">{lick.title}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{lick.idea}</p>
        </div>
        {!locked && (
          <button type="button" onClick={() => onSave(lick.id)} aria-label={saved ? "Remove from saved licks" : "Save lick"} data-testid={`lick-save-${lick.id}`}
            className={`rounded-full border p-2 transition-colors ${saved ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:text-primary"}`}>
            <Heart size={16} fill={saved ? "currentColor" : "none"} />
          </button>
        )}
      </div>
      <div className="mt-3">
        <StaffViewer clef={lick.clef || "treble"} notes={lick.notes} width={480} showLabels tabFrets={lick.tab} activeIndex={step} testId={`lick-staff-${lick.id}`} />
      </div>
      {locked ? (
        <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground"><Lock size={12} /> Unlock a Gospel course to play, loop and save this lick.</p>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {step >= 0
              ? <Button size="sm" variant="outline" className="rounded-full" onClick={stop} data-testid={`lick-stop-${lick.id}`}><Square size={14} className="mr-1.5" /> Stop</Button>
              : <Button size="sm" className="rounded-full bg-primary text-primary-foreground" onClick={play} data-testid={`lick-play-${lick.id}`}><Play size={14} className="mr-1.5" /> Play</Button>}
            <button type="button" onClick={() => setLoop((v) => !v)} className={chip(loop)} data-testid={`lick-loop-${lick.id}`}><Repeat size={12} className="mr-1 inline" /> Loop {loop ? "on" : "off"}</button>
            {track && <button type="button" onClick={() => { stop(); setBand((v) => !v); }} className={chip(band)} data-testid={`lick-band-${lick.id}`} title={`Backing: ${track.title}`}><Music size={12} className="mr-1 inline" /> {band ? `With band: ${track.title}` : "With backing band"}</button>}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">Practice speed</span>
            {SPEEDS.map((s) => <button key={s} type="button" onClick={() => setSpeed(s)} className={chip(speed === s)} data-testid={`lick-speed-${s}-${lick.id}`}>{s}%</button>)}
            <button type="button" onClick={() => onMaster(lick.id)} className={`${chip(mastered)} ml-auto`} data-testid={`lick-master-${lick.id}`}>
              <CheckCircle2 size={12} className="mr-1 inline" /> {mastered ? "Mastered" : "Mark mastered"}
            </button>
          </div>
          <Link to={`/lessons/${lick.lessonId}`} className="mt-3 inline-block text-xs text-primary hover:underline" data-testid={`lick-lesson-${lick.id}`}>From the lesson →</Link>
        </>
      )}
    </div>
  );
};

export default function GospelLicks() {
  const { user, purchases } = useAuth();
  const owns = (purchases || []).some((k) => GOSPEL_COURSES.some((c) => c.key === k));
  const [inst, setInst] = useState("pianists");
  const [level, setLevel] = useState("All");
  const [onlySaved, setOnlySaved] = useState(false);
  const [bpm, setBpm] = useState(72);
  const [saved, setSaved] = useState([]);
  const [mastered, setMastered] = useState([]);
  useEffect(() => { if (owns) api.get("/me/licks").then(({ data }) => { setSaved(data.ids); setMastered(data.mastered || []); }).catch(() => {}); }, [owns]);
  const persist = (ids, mast, msg) => api.put("/me/licks", { ids, mastered: mast }).then(() => toast.success(msg)).catch(() => toast.error("Could not save — please try again"));
  const toggleSave = (id) => {
    const next = saved.includes(id) ? saved.filter((x) => x !== id) : [...saved, id];
    setSaved(next);
    persist(next, mastered, next.includes(id) ? "Lick saved to your account" : "Lick removed");
  };
  const toggleMaster = (id) => {
    const next = mastered.includes(id) ? mastered.filter((x) => x !== id) : [...mastered, id];
    setMastered(next);
    persist(saved, next, next.includes(id) ? "Marked as mastered — well done!" : "Unmarked");
  };
  const all = GOSPEL_LICKS.filter((l) => l.inst === inst);
  const list = all.filter((l) => (level === "All" || l.level === level) && (!onlySaved || saved.includes(l.id)));
  const shown = owns ? list : list.slice(0, 2);
  const masteredCount = all.filter((l) => mastered.includes(l.id)).length;
  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-8 lg:py-14" data-testid="gospel-licks-page">
        <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-primary"><Church size={14} /> Gospel courses</p>
        <h1 className="mt-3 font-serif text-4xl tracking-tight sm:text-5xl">Gospel Lick Library</h1>
        <p className="mt-4 max-w-2xl text-muted-foreground">Every gospel and blues lick from the Gospel courses. Hear it, slow it down, loop it on its own or with the song's backing band, and mark it mastered when it's automatic — that's what makes improvising sound free.</p>
        <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="lick-filters">
          {[["pianists", "Piano"], ["guitarists", "Guitar"]].map(([id, label]) => <button key={id} type="button" className={chip(inst === id)} onClick={() => setInst(id)} data-testid={`lick-inst-${id}`}>{label}</button>)}
          <span className="mx-2 h-5 w-px bg-border" />
          {["All", "Beginner", "Intermediate"].map((lv) => <button key={lv} type="button" className={chip(level === lv)} onClick={() => setLevel(lv)} data-testid={`lick-level-${lv.toLowerCase()}`}>{lv}</button>)}
          {owns && <button type="button" className={chip(onlySaved)} onClick={() => setOnlySaved((v) => !v)} data-testid="lick-only-saved"><Heart size={12} className="mr-1 inline" /> Saved ({saved.length})</button>}
          {owns && <span className="ml-auto rounded-full bg-primary/10 px-3 py-1 text-xs text-primary" data-testid="lick-mastered-count"><CheckCircle2 size={12} className="mr-1 inline" /> Mastered {masteredCount} of {all.length}</span>}
        </div>
        {owns && (
          <div className="mt-5 flex max-w-sm items-center gap-4" data-testid="lick-tempo">
            <span className="text-sm text-muted-foreground">Tempo</span>
            <Slider value={[bpm]} min={40} max={140} step={2} onValueChange={([v]) => setBpm(v)} data-testid="lick-tempo-slider" />
            <span className="w-20 whitespace-nowrap font-mono text-sm" data-testid="lick-tempo-value">{bpm} bpm</span>
          </div>
        )}
        {!owns && (
          <div className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-primary/40 bg-primary/10 p-5" data-testid="lick-locked">
            <p className="text-sm text-muted-foreground"><Lock size={14} className="mr-1.5 inline text-primary" /> The Lick Library comes with any Gospel course: £300 per course, or any two for £500. Here's a preview.</p>
            <Link to={user ? "/payment#gospel" : "/login"} data-testid="lick-unlock-link"><Button className="rounded-full bg-primary text-primary-foreground">{user ? "Unlock a Gospel course" : "Log in"}</Button></Link>
          </div>
        )}
        <div className="mt-8 grid gap-5 md:grid-cols-2">
          {shown.map((l) => <LickCard key={l.id} lick={l} bpm={bpm} saved={saved.includes(l.id)} mastered={mastered.includes(l.id)} onSave={toggleSave} onMaster={toggleMaster} locked={!owns} />)}
        </div>
        {owns && !shown.length && <p className="mt-8 text-sm text-muted-foreground" data-testid="lick-empty">No saved licks yet — tap the heart on any lick to save it.</p>}
      </main>
    </div>
  );
}
